import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { donationsCardBlockName } from '@gik/create/components';
import PageFeatureCard, { IPageFeatureCardProps } from '@gik/create/components/PageFeatureCard/PageFeatureCard';
import i18n from '@gik/i18n';
import { translationKeys } from '@gik/create/i18n/en';
import { PropsWithClassName } from '@gik/core/utils/ReactUtils';
import noop from '@gik/core/utils/noop';
import { usePrevious } from 'react-use';
import DonationsContainer from '@gik/create/components/DonationsContainer/DonationsContainer';
import {
  GoFundMeDonationFormValueType,
  PayPalDonationFormValueType,
} from '@gik/create/components/DonationProvider/DonationProvider';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/hooks/BreakpointHooks';
import { GoFundMeFormInputNames } from '@gik/create/enums/GoFundMeFormInputNames';
import { PayPalFormInputNames } from '@gik/create/enums/PayPalFormInputNames';

export type DonationsValues = PayPalDonationFormValueType &
  GoFundMeDonationFormValueType & {
    enabled: boolean;
  };

export interface IDonationsCardProps extends Pick<IPageFeatureCardProps, 'inputName'> {
  onChange?: (values: DonationsValues) => void;
  value: DonationsValues;
  disabled?: boolean;
}

export default function DonationsCard({
  onChange = noop,
  value,
  className,
  inputName,
  disabled = false,
}: PropsWithClassName<IDonationsCardProps>) {
  const bem = bemBlock(donationsCardBlockName);

  const isMd = useBreakpoint(Breakpoint.MD);

  // we need to detect when isMd, isPayPalValueValid and isGoFundMeValueValid are changed
  // when they are changed we need to force Accordion to recalculate height
  // initial validation error mesage is shown on blur
  // that's why we need to pass recalculateHeightCallback down to DonationProvider
  // and execute it there when blur state is changed
  const prevIsMdUp = usePrevious(isMd);
  const prevIsPayPalValueValid = usePrevious(value.isPayPalValueValid);
  const prevIsGoFundMeValueValid = usePrevious(value.isGoFundMeValueValid);

  const recalculateHeightFnRef = React.useRef<() => void>(noop);

  React.useEffect(() => {
    if (
      prevIsMdUp !== isMd ||
      prevIsPayPalValueValid !== value.isPayPalValueValid ||
      prevIsGoFundMeValueValid !== value.isGoFundMeValueValid
    ) {
      recalculateHeightFnRef.current?.();
    }
  }, [isMd, prevIsMdUp, value, prevIsPayPalValueValid, prevIsGoFundMeValueValid]);

  return (
    <PageFeatureCard
      className={bem(null, null, className)}
      icon="FundraisingThin"
      title={i18n.t(translationKeys.FundraisingTitle)}
      description={i18n.t(translationKeys.FundraisingDescription)}
      notes={i18n.t(translationKeys.FundraisingNotes)}
      onChange={enabled =>
        onChange({
          ...value,
          enabled,
        })
      }
      value={value.enabled}
      inputName={inputName}
      disabled={disabled}
      recalculateHeightCallback={fn => (recalculateHeightFnRef.current = fn)}
    >
      <DonationsContainer
        asForm={false}
        onChange={donationValues =>
          onChange({
            ...value,
            [GoFundMeFormInputNames.PageURL]: donationValues[GoFundMeFormInputNames.PageURL],
            [PayPalFormInputNames.EmailAddress]: donationValues[PayPalFormInputNames.EmailAddress],
            isPayPalValueValid: donationValues.isPayPalValueValid,
            isGoFundMeValueValid: donationValues.isGoFundMeValueValid,
          })
        }
        value={value}
        variant="page-settings"
        direction={isMd ? 'horizontal' : 'vertical'}
        recalculateHeightCallback={recalculateHeightFnRef.current}
      />
    </PageFeatureCard>
  );
}
